import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Button,
  Flex,
  Heading,
  Image,
  Link,
  SimpleGrid,
  Spinner,
  Stack,
  Text
} from '@chakra-ui/react';
import { useNavigate, useParams } from 'react-router-dom';
import PageLayout from './PageLayout';
import { API_BASE, resolveMediaUrl } from '../api';

const PAGE_ACCENT = '#93C8E6';
const PAGE_BACKGROUND = '#EEF3F7';

const DynamicContentPage = ({ slug: slugProp }) => {
  const navigate = useNavigate();
  const [page, setPage] = useState(null);
  const [content, setContent] = useState(null);
  const [children, setChildren] = useState([]);
  const [parentPage, setParentPage] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const params = useParams();
  const slug = slugProp || params.slug;

  const layoutProps = {
    accent: PAGE_ACCENT,
    background: PAGE_BACKGROUND,
    watermark: '/maconaria.png'
  };

  useEffect(() => {
    const fetchPage = async () => {
      setIsLoading(true);
      try {
        const response = await fetch(`${API_BASE}/pagina?slug=${slug}&id_instituicao=1`);
        const data = await response.json();
        const result = (data.result && data.result[0]) || null;
        setPage(result);

        if (result && result.conteudo_json) {
          const parsed = JSON.parse(result.conteudo_json);
          setContent({
            settings: { showContent: true, showChildren: false },
            ...parsed
          });
        } else {
          setContent({ settings: { showContent: true, showChildren: false }, blocks: [] });
        }

        if (result?.parent_id) {
          try {
            const parentResponse = await fetch(`${API_BASE}/pagina?id_pagina=${result.parent_id}`);
            const parentData = await parentResponse.json();
            const parentResult = (parentData.result && parentData.result[0]) || null;
            setParentPage(parentResult);
          } catch (error) {
            setParentPage(null);
          }
        } else {
          setParentPage(null);
        }

        if (result?.id_pagina) {
          const childrenResponse = await fetch(`${API_BASE}/paginas?status=published&id_instituicao=1&parent_id=${result.id_pagina}`);
          const childrenData = await childrenResponse.json();
          setChildren(childrenData.result || []);
        } else {
          setChildren([]);
        }
      } catch (error) {
        setContent({ settings: { showContent: true, showChildren: false }, blocks: [] });
        setParentPage(null);
      } finally {
        setIsLoading(false);
      }
    };

    fetchPage();
  }, [slug]);

  const theme = useMemo(() => content?.theme || {}, [content]);
  const headingSizeMap = {
    sm: 'md',
    md: 'lg',
    lg: 'xl',
    xl: '2xl'
  };
  const textSizeMap = {
    sm: 'sm',
    md: 'md',
    lg: 'lg',
    xl: 'xl'
  };

  const resolveIconUrl = (icone) => {
    if (!icone) return '';
    if (icone.startsWith('media:')) {
      return resolveMediaUrl(icone.replace('media:', ''));
    }
    if (icone.startsWith('http') || icone.startsWith('/')) {
      return icone;
    }
    return `/icones/${icone}`;
  };

  const normalizeHtml = (html) => {
    if (!html) return '';
    return html
      .replace(/src=(['"])(\/)?Media\//g, `src=$1${API_BASE}/Media/`)
      .replace(/href=(['"])(\/)?Media\//g, `href=$1${API_BASE}/Media/`);
  };

  if (isLoading) {
    return (
      <PageLayout title="Carregando" onBack={() => navigate('/')} {...layoutProps}>
        <Flex align="center" justify="center" minH="200px">
          <Spinner size="lg" />
        </Flex>
      </PageLayout>
    );
  }

  if (!page) {
    return (
      <PageLayout title="Pagina nao encontrada" onBack={() => navigate('/')} {...layoutProps}>
        <Text>Essa pagina ainda nao foi publicada.</Text>
      </PageLayout>
    );
  }

  const handleChildClick = (child) => {
    if (child?.tipo === 'route' && child?.rota) {
      navigate(child.rota);
      return;
    }
    if (child?.slug) {
      navigate(`/p/${child.slug}`);
    }
  };

  const shouldShowContent = content?.settings?.showContent !== false;
  const showChildrenSetting =
    typeof content?.settings?.showChildren === 'boolean'
      ? content.settings.showChildren
      : children.length > 0;
  const hasContent = Boolean(content?.blocks?.length);
  const showContent = shouldShowContent && hasContent;
  const shouldShowChildren = showChildrenSetting || (!hasContent && children.length > 0);

  const renderBlock = (block) => {
    const align = block.style?.align || 'left';
    const alignItemsMap = { left: 'flex-start', center: 'center', right: 'flex-end', justify: 'flex-start' };
    const textAlignMap = { left: 'left', center: 'center', right: 'right', justify: 'justify' };
    const alignItems = alignItemsMap[align] || 'flex-start';
    const textAlign = textAlignMap[align] || 'left';
    const textColor = block.style?.textColor || 'gray.700';
    const bgColor = block.style?.bgColor || 'white';
    const fontSize = textSizeMap[block.style?.fontSize] || 'md';

    if (block.type === 'hero') {
      const imageUrl = block.data?.image?.arquivo ? resolveMediaUrl(block.data.image.arquivo) : '';
      const subheadingColor = block.style?.subTextColor
        || (textColor === '#FFFFFF' || textColor === 'white' ? 'whiteAlpha.800' : 'gray.600');
      return (
        <Flex
          key={block.id}
          direction={{ base: 'column', lg: 'row' }}
          gap={8}
          align="center"
          bg={bgColor}
          borderRadius="20px"
          p={{ base: 6, lg: 8 }}
          boxShadow="0 18px 40px rgba(0,0,0,0.12)"
        >
          <Stack spacing={4} flex="1" textAlign={textAlign}>
            <Heading
              size={headingSizeMap[block.style?.fontSize] || 'lg'}
              color={textColor}
            >
              {block.data?.heading}
            </Heading>
            <Text fontSize={fontSize} color={subheadingColor}>
              {block.data?.subheading}
            </Text>
            {block.data?.buttonLabel && block.data?.buttonUrl && (
              <Button
                as={Link}
                href={block.data.buttonUrl}
                colorScheme="yellow"
                bg={theme.accent || '#C9A24C'}
                color="white"
                _hover={{ opacity: 0.9 }}
                width="fit-content"
              >
                {block.data.buttonLabel}
              </Button>
            )}
          </Stack>
          {imageUrl && (
            <Image
              src={imageUrl}
              alt={block.data?.image?.alt_texto || 'Imagem'}
              borderRadius="16px"
              maxH="260px"
              objectFit="cover"
            />
          )}
        </Flex>
      );
    }

    if (block.type === 'richText') {
      const html = normalizeHtml(block.data?.html || '');
      return (
        <Box
          key={block.id}
          bg={bgColor}
          borderRadius="18px"
          p={{ base: 4, md: 6 }}
          boxShadow="0 14px 30px rgba(0,0,0,0.1)"
          textAlign={textAlign}
        >
          <Box
            color={textColor}
            fontSize={fontSize}
            lineHeight="1.7"
            sx={{
              '& p, & li, & h1, & h2, & h3, & h4, & h5, & h6': { textAlign },
              '& ul, & ol': {
                marginInlineStart: '1.3rem',
                paddingInlineStart: '1rem',
                textAlign
              },
              '& li': {
                marginBottom: '0.35rem',
                lineHeight: '1.8',
                textAlign,
                wordBreak: 'break-word'
              },
              '& li::marker': {
                color: 'currentColor',
                fontWeight: 600
              }
            }}
            dangerouslySetInnerHTML={{ __html: html }}
          />
        </Box>
      );
    }

    if (block.type === 'image') {
      const imageUrl = block.data?.image?.arquivo ? resolveMediaUrl(block.data.image.arquivo) : '';
      return (
        <Stack key={block.id} spacing={3} alignItems={alignItems} textAlign={textAlign}>
          {imageUrl && (
            <Image
              src={imageUrl}
              alt={block.data?.image?.alt_texto || 'Imagem'}
              borderRadius="18px"
              maxH={block.data?.size === 'lg' ? '420px' : block.data?.size === 'sm' ? '220px' : '320px'}
              objectFit="cover"
            />
          )}
          {block.data?.caption && (
            <Text fontSize="sm" color={textColor}>{block.data.caption}</Text>
          )}
        </Stack>
      );
    }

    if (block.type === 'cta') {
      const ctaBg = block.style?.bgColor || 'gray.900';
      return (
        <Flex
          key={block.id}
          direction={{ base: 'column', md: 'row' }}
          align="center"
          justify="space-between"
          gap={6}
          bg={ctaBg}
          color={textColor || 'white'}
          borderRadius="20px"
          p={{ base: 6, md: 8 }}
          textAlign={textAlign}
        >
          <Box>
            <Heading size={headingSizeMap[block.style?.fontSize] || 'md'} mb={2}>
              {block.data?.title}
            </Heading>
            <Text color="gray.300" fontSize={fontSize}>{block.data?.subtitle}</Text>
          </Box>
          {block.data?.buttonLabel && block.data?.buttonUrl && (
            <Button
              as={Link}
              href={block.data.buttonUrl}
              colorScheme="yellow"
              bg={theme.accent || '#C9A24C'}
              color="white"
              _hover={{ opacity: 0.9 }}
            >
              {block.data.buttonLabel}
            </Button>
          )}
        </Flex>
      );
    }

    return null;
  };

  return (
    <PageLayout
      title={page.titulo}
      onBack={() => {
        if (parentPage?.slug) {
          navigate(`/p/${parentPage.slug}`);
          return;
        }
        navigate('/');
      }}
      {...layoutProps}
    >
      <Stack spacing={6}>
        {page.resumo && (
          <Text fontSize="md" color="gray.600">{page.resumo}</Text>
        )}
        {shouldShowChildren && children.length > 0 && (
          <Box>
            <Text fontSize="lg" fontWeight="bold" mb={4}>Subpáginas</Text>
            <SimpleGrid columns={{ base: 3, md: 3, lg: 3 }} spacing={4}>
              {children.map((child) => (
                <Flex
                  key={child.id_pagina}
                  direction="column"
                  align="center"
                  justify="center"
                  gap={3}
                  bg="white"
                  borderRadius="20px"
                  p={4}
                  minH="140px"
                  boxShadow="0 12px 26px rgba(15,23,42,0.12)"
                  cursor="pointer"
                  onClick={() => handleChildClick(child)}
                  _hover={{ transform: 'translateY(-2px)', boxShadow: '0 16px 30px rgba(15,23,42,0.18)' }}
                >
                  <Box
                    width="64px"
                    height="64px"
                    borderRadius="18px"
                    bg="linear-gradient(135deg, rgba(147,200,230,0.9), rgba(147,200,230,0.35))"
                    display="flex"
                    alignItems="center"
                    justifyContent="center"
                    boxShadow="inset 0 0 0 1px rgba(255,255,255,0.7)"
                  >
                    {child.icone && (
                      <Image
                        src={resolveIconUrl(child.icone)}
                        alt={child.titulo}
                        boxSize="32px"
                        objectFit="contain"
                      />
                    )}
                  </Box>
                  <Text fontWeight="600" fontSize="sm" color="gray.700" textAlign="center" noOfLines={2}>
                    {child.titulo}
                  </Text>
                </Flex>
              ))}
            </SimpleGrid>
          </Box>
        )}
        {showContent ? (
          content.blocks.map(renderBlock)
        ) : null}
        {!showContent && children.length === 0 && (
          <Text>Essa pagina ainda nao tem conteudo.</Text>
        )}
      </Stack>
    </PageLayout>
  );
};

const DynamicPage = () => {
  return <DynamicContentPage />;
};

export { DynamicContentPage };
export default DynamicPage;
